import { useState, useMemo } from 'react';
import { PrefectureHeader } from './components/PrefectureHeader';
import { NaraMap } from './components/NaraMap';
import type { ShopMarker } from './components/NaraMap';
import { ShopList } from './components/ShopList';
import { ArtistList } from './components/ArtistList';
import { EventList } from './components/EventList';
import { usePrefectureShops } from '../../hooks/usePrefectureShops';
import { BottomNav } from '../../components/feature/BottomNav';

export default function PrefecturePage() {
  const [selectedShopId, setSelectedShopId] = useState<string | number | null>(null);
  const { shops, loading: shopsLoading } = usePrefectureShops('nara');

  // NaraMap用にShopMarker型へ変換（lat/lngがnullのものは除外）
  const mapMarkers = useMemo<ShopMarker[]>(
    () =>
      shops
        .filter((s) => s.lat !== null && s.lng !== null)
        .map((s) => ({
          id: s.id,
          name: s.name,
          category: s.category,
          color: s.color,
          lat: s.lat as number,
          lng: s.lng as number,
        })),
    [shops]
  );

  const selectedMarker = useMemo(
    () => mapMarkers.find((m) => m.id === selectedShopId) ?? null,
    [mapMarkers, selectedShopId]
  );

  const categoryLegend = useMemo(() => {
    const seen = new Map<string, string>();
    shops.forEach((s) => {
      if (!seen.has(s.category)) seen.set(s.category, s.color);
    });
    return Array.from(seen.entries()).map(([label, color]) => ({ label, color }));
  }, [shops]);

  return (
    <div className="bg-black min-h-screen pb-24">
      {/* Background noise */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/d8e1192effeb065d9828bb3d391cea6f/487e2600aaa2d450ac57cbe9e20dd89d.png"
              alt="DigDim Logo"
              className="w-7 h-7"
            />
            <span
              className="font-display text-lg text-white tracking-wider"
              style={{ textShadow: '0 0 12px rgba(0,255,65,0.3)' }}
            >
              DigDim
            </span>
          </a>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 tracking-widest">NARA</span>
            <div className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse" />
          </div>
        </div>
      </div>

      {/* Header */}
      <PrefectureHeader />

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-8 py-8 lg:py-12">
        {/* Map Area */}
        <div className="mb-8 lg:mb-10">
          <div
            className="bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
            style={{ boxShadow: '0 0 40px rgba(0, 255, 65, 0.05)' }}
          >
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-green" style={{ boxShadow: '0 0 8px #00ff41' }} />
                <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Map View</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-green/10 border border-neon-green/20">
                <i className="ri-map-pin-line text-neon-green text-xs" />
                <span className="text-neon-green text-[10px] font-bold tracking-wider">
                  {shopsLoading ? '...' : `${mapMarkers.length} SPOTS`}
                </span>
              </div>
            </div>

            <div className="max-w-[500px] mx-auto">
              <NaraMap
                shops={mapMarkers}
                selectedShop={selectedMarker}
                onSelectShop={(m) => setSelectedShopId(m ? m.id : null)}
              />
            </div>

            <div className="mt-4 lg:mt-6 flex flex-wrap gap-3 lg:gap-4 justify-center">
              {categoryLegend.map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }}
                  />
                  <span className="text-gray-500 text-[10px] tracking-wider">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-white/[0.02] border border-white/5">
            <i className="ri-information-line text-gray-600 text-sm mt-0.5" />
            <p className="text-gray-600 text-xs leading-relaxed">
              マップ上のマーカーをクリックすると店舗情報が表示されます。今後、新しい店舗が随時追加されます。
            </p>
          </div>
        </div>

        {/* Shop List */}
        <div
          id="shop-list"
          className="bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
          style={{ boxShadow: '0 0 40px rgba(255, 0, 110, 0.03)' }}
        >
          <ShopList
            shops={shops}
            loading={shopsLoading}
            selectedShopId={selectedShopId}
            onSelectShopId={setSelectedShopId}
          />
        </div>

        {/* Artist List */}
        <div
          id="artist-list"
          className="mt-8 lg:mt-10 bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
          style={{ boxShadow: '0 0 40px rgba(255, 0, 110, 0.03)' }}
        >
          <ArtistList prefectureId="nara" />
        </div>

        {/* Event List */}
        <div
          id="event-list"
          className="mt-8 lg:mt-10 bg-[#0a0a0a] rounded-2xl border border-white/5 p-4 sm:p-6 lg:p-8"
          style={{ boxShadow: '0 0 40px rgba(0, 217, 255, 0.03)' }}
        >
          <EventList />
        </div>

        {/* Coming Soon */}
        <div className="mt-12 lg:mt-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 rounded-full bg-neon-blue" style={{ boxShadow: '0 0 10px #00d9ff' }} />
            <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">Other Prefectures</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { name: '東京都', nameEn: 'TOKYO', color: '#ff006e' },
              { name: '大阪府', nameEn: 'OSAKA', color: '#00d9ff' },
              { name: '京都府', nameEn: 'KYOTO', color: '#00ff41' },
              { name: '福岡県', nameEn: 'FUKUOKA', color: '#b537f2' },
              { name: '北海道', nameEn: 'HOKKAIDO', color: '#ff006e' },
              { name: '愛知県', nameEn: 'AICHI', color: '#00d9ff' },
            ].map((pref) => (
              <div
                key={pref.nameEn}
                className="relative p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 cursor-pointer group overflow-hidden"
              >
                <div
                  className="absolute top-0 right-0 w-16 h-16 opacity-5 group-hover:opacity-10 transition-opacity duration-300 rounded-full blur-xl"
                  style={{ background: pref.color }}
                />
                <span className="font-display text-lg text-white/40 group-hover:text-white/70 tracking-wider transition-colors duration-300 block">
                  {pref.nameEn}
                </span>
                <span className="text-gray-600 text-[10px] block mt-1">{pref.name}</span>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-[9px] text-gray-700 tracking-wider">COMING SOON</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
